import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    Award,
    ShieldCheck,
    Download,
    RefreshCw,
    TrendingUp,
    Shield,
    KeyRound,
    Cpu,
    FileSearch,
    Mail,
    CheckCircle2,
    AlertTriangle,
    Calendar,
    FileText,
    ArrowRight,
    Lock,
    Printer
} from 'lucide-react';
import { backendURL } from '../config';

export default function ExecutiveBrief() {
    const [loading, setLoading] = useState(true);
    const [briefData, setBriefData] = useState(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);

    const fetchBrief = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/executive-brief`);
            if (res.data && res.data.success) {
                setBriefData(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch executive brief data:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBrief();
    }, []);

    const handleDownloadPDF = () => {
        if (!briefData) return;
        setGeneratingPdf(true);

        try {
            const doc = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pageHeight = doc.internal.pageSize.getHeight();
            const pageWidth = doc.internal.pageSize.getWidth();

            // Header Banner (Dark Theme)
            doc.setFillColor(7, 11, 20); // #070b14
            doc.rect(0, 0, pageWidth, 42, "F");

            // Brand Title
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.text("QUERYTEL SENTINEL", 14, 18);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(148, 163, 184); // slate-400
            doc.text("Executive Cybersecurity Posture Brief & Board Report", 14, 25);
            doc.text(`Generated: ${new Date(briefData.generatedAt || Date.now()).toUTCString()} | Org: ${briefData.organizationName || 'QueryTel Inc.'}`, 14, 32);

            // Grade Badge Box (Right Side)
            doc.setFillColor(15, 23, 42);
            doc.roundedRect(pageWidth - 55, 8, 42, 26, 3, 3, "F");
            doc.setTextColor(56, 189, 248); // sky-400
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.text("OVERALL POSTURE", pageWidth - 50, 15);
            doc.setFontSize(18);
            doc.setTextColor(52, 211, 153); // emerald-400
            doc.text(`${briefData.overallGrade} (${briefData.overallScore}%)`, pageWidth - 48, 27);

            // Section 1: Executive Summary
            doc.setTextColor(15, 23, 42);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.text("1. Executive Summary & Defense Posture", 14, 52);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(51, 65, 85);
            const summaryText = `During the current reporting period, QueryTel Sentinel maintained an overall cybersecurity posture rating of ${briefData.overallGrade} (${briefData.overallScore}% Defended). Across the five integrated security layers (FortiGate Perimeter, Microsoft Entra ID, Microsoft Defender XDR, ThreatDown EDR, and Microsoft Purview), threat containment remained optimal with 100% of critical lateral movement and C2 beaconing attempts dropped at the perimeter edge.`;
            doc.text(doc.splitTextToSize(summaryText, pageWidth - 28), 14, 60);

            // Section 2: 5 Security Pillars Breakdown Table
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(15, 23, 42);
            doc.text("2. Multi-Vendor Security Pillar Evaluation", 14, 82);

            const pillarRows = (briefData.pillarScores || []).map(p => [
                p.name,
                p.vendor,
                `${p.grade} (${p.score}%)`,
                p.status,
                p.finding
            ]);

            autoTable(doc, {
                startY: 86,
                head: [["Security Pillar", "Engine", "Grade", "Status", "Executive Finding"]],
                body: pillarRows,
                theme: "striped",
                headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", fontSize: 9 },
                bodyStyles: { fontSize: 8.5, textColor: [30, 41, 59] },
                columnStyles: {
                    0: { cellWidth: 38 },
                    1: { cellWidth: 28 },
                    2: { cellWidth: 22 },
                    3: { cellWidth: 24 },
                    4: { cellWidth: "auto" }
                }
            });

            // Section 3: Compliance Framework Alignment
            let nextY = doc.lastAutoTable.finalY + 12;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(15, 23, 42);
            doc.text("3. Regulatory & Compliance Alignment", 14, nextY);

            const compRows = (briefData.complianceAlignment || []).map(c => [
                c.framework,
                c.complianceRate,
                c.status
            ]);

            autoTable(doc, {
                startY: nextY + 4,
                head: [["Compliance Standard", "Compliance Score", "Attestation Status"]],
                body: compRows,
                theme: "striped",
                headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", fontSize: 9 },
                bodyStyles: { fontSize: 8.5, textColor: [30, 41, 59] },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 40 },
                    2: { cellWidth: 40 }
                }
            });

            // Section 4: Prioritized Action Items
            nextY = doc.lastAutoTable.finalY + 12;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(13);
            doc.setTextColor(15, 23, 42);
            doc.text("4. Prioritized Executive Remediation Actions", 14, nextY);

            const actionRows = (briefData.executiveActionItems || []).map(a => [
                a.priority,
                a.action,
                a.owner
            ]);

            autoTable(doc, {
                startY: nextY + 4,
                head: [["Priority Window", "Mandated Action Item", "Assigned Owner"]],
                body: actionRows,
                theme: "striped",
                headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", fontSize: 9 },
                bodyStyles: { fontSize: 8.5, textColor: [30, 41, 59] },
                columnStyles: {
                    0: { cellWidth: 42 },
                    1: { cellWidth: "auto" },
                    2: { cellWidth: 40 }
                }
            });

            // Executive Sign-off block
            nextY = doc.lastAutoTable.finalY + 14;
            if (nextY + 25 < pageHeight) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(71, 85, 105);
                doc.text("Report Authorized By: Chief Information Security Officer (CISO) / SecOps Lead", 14, nextY);
                doc.setDrawColor(203, 213, 225);
                doc.line(14, nextY + 8, 90, nextY + 8);
                doc.setFont("helvetica", "italic");
                doc.setFontSize(8);
                doc.text("QueryTel Sentinel Automated Executive Brief Engine", 14, nextY + 13);
            }

            doc.save(`QueryTel_Sentinel_Executive_Brief_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (err) {
            console.error("PDF generation failed:", err);
            alert("Failed to generate PDF: " + err.message);
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (loading && !briefData) {
        return (
            <div className="min-h-screen bg-[#070b14] flex items-center justify-center text-slate-400 font-mono text-xs">
                <RefreshCw className="w-5 h-5 animate-spin mr-2 text-indigo-400" />
                Aggregating Executive Security Posture Across All Pillars...
            </div>
        );
    }

    const pillarScores = briefData?.pillarScores || [];
    const executiveActionItems = briefData?.executiveActionItems || [];
    const complianceAlignment = briefData?.complianceAlignment || [];

    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-indigo-600/10 border border-indigo-500/20 text-indigo-400">
                        <Award className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                Executive Security Posture Scorecard
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 font-mono">
                                BOARD REPORT
                            </span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-mono">
                                GRADE {briefData?.overallGrade || 'A-'}
                            </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                            C-Level Governance Brief: Multi-Vendor Defense Synthesis, Compliance Attestations &amp; 1-Click Board-Ready PDF Export
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={fetchBrief}
                        disabled={loading}
                        className="px-3 py-2 bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] rounded-xl text-xs font-semibold text-slate-200 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 text-indigo-400 ${loading ? 'animate-spin' : ''}`} />
                        <span>Recalculate Score</span>
                    </button>

                    <button
                        onClick={handleDownloadPDF}
                        disabled={generatingPdf}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-bold text-white transition-all flex items-center gap-2 shadow-lg shadow-indigo-600/20 font-mono"
                    >
                        <Download className="w-4 h-4" />
                        <span>{generatingPdf ? 'Generating PDF...' : 'Download Board-Ready PDF'}</span>
                    </button>
                </div>
            </div>

            {/* Overall Posture Score Hero Card */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-[#0d1424] via-[#11192e] to-[#0d1424] border border-white/[0.08] flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-xl relative overflow-hidden">
                <div className="space-y-2 relative z-10">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-indigo-400">
                        Enterprise Cyber Health Assessment
                    </span>
                    <h2 className="text-2xl font-black text-slate-100">
                        QueryTel Sentinel Overall Security Rating: {briefData?.overallGrade}
                    </h2>
                    <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
                        Continuous telemetry aggregation across FortiGate perimeter, Microsoft Entra ID identity sensors, Microsoft Defender XDR, ThreatDown EDR, and Microsoft Purview compliance engines indicates an enterprise defense score of <strong>{briefData?.overallScore}%</strong>. Zero active lateral movement or uncontained C2 beacons observed.
                    </p>
                    <div className="flex items-center gap-3 pt-2 text-[11px] font-mono text-slate-400">
                        <span>Report Period: <strong>Live Real-Time</strong></span>
                        <span>•</span>
                        <span>Status: <strong className="text-emerald-400">{briefData?.status}</strong></span>
                    </div>
                </div>

                <div className="flex items-center gap-6 shrink-0 relative z-10">
                    <div className="w-24 h-24 rounded-2xl bg-indigo-500/10 border border-indigo-500/30 flex flex-col items-center justify-center p-3 text-center shadow-inner">
                        <span className="text-3xl font-black text-emerald-400">{briefData?.overallGrade}</span>
                        <span className="text-[10px] font-bold text-slate-300 font-mono mt-0.5">{briefData?.overallScore}% DEFENDED</span>
                    </div>
                </div>
            </div>

            {/* 5 Security Pillars Grid */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5 text-indigo-400" />
                        <span>Core Defense Pillars Breakdown (5 Security Layers)</span>
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">Continuous Real-Time Scoring</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3.5">
                    {pillarScores.map(pillar => (
                        <div key={pillar.id} className="p-4 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-3 flex flex-col justify-between">
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span
                                        className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
                                        style={{ backgroundColor: `${pillar.color}20`, color: pillar.color, border: `1px solid ${pillar.color}40` }}
                                    >
                                        {pillar.vendor}
                                    </span>
                                    <span className="text-xs font-mono font-bold text-emerald-400">
                                        {pillar.grade} ({pillar.score}%)
                                    </span>
                                </div>

                                <h3 className="text-xs font-bold text-slate-100 line-clamp-1">{pillar.name}</h3>

                                <div className="space-y-1 pt-1">
                                    {pillar.kpis.map((kpi, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-[10px] font-mono p-1 rounded bg-white/[0.02]">
                                            <span className="text-slate-400">{kpi.label}:</span>
                                            <span className="text-slate-200 font-bold">{kpi.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <p className="text-[10px] text-slate-400 line-clamp-2 pt-2 border-t border-white/[0.04]">
                                {pillar.finding}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Regulatory Compliance Alignment & Prioritized Action Items */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Compliance Standards */}
                <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-3.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span>Regulatory Framework Compliance Alignment</span>
                    </span>

                    <div className="space-y-2">
                        {complianceAlignment.map((c, idx) => (
                            <div key={idx} className="p-3 rounded-xl bg-black/40 border border-white/[0.04] flex items-center justify-between text-xs">
                                <div>
                                    <span className="font-bold text-slate-200 block">{c.framework}</span>
                                    <span className="text-[10px] font-mono text-slate-400">Continuous Sentinel Telemetry Mapping</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="text-xs font-mono font-bold text-emerald-400">{c.complianceRate}</span>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                        {c.status}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Prioritized Executive Remediation Actions */}
                <div className="p-5 rounded-xl bg-[#0d1424] border border-white/[0.06] space-y-3.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-200 font-mono flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <span>Prioritized Leadership Action Items</span>
                    </span>

                    <div className="space-y-2">
                        {executiveActionItems.map((item, idx) => (
                            <div key={idx} className="p-3 rounded-xl bg-black/40 border border-white/[0.04] space-y-1.5 text-xs">
                                <div className="flex items-center justify-between">
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono ${
                                        item.priority.includes('Immediate') ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                        item.priority.includes('High') ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                                        'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                    }`}>
                                        {item.priority}
                                    </span>
                                    <span className="text-[10px] font-mono text-slate-400">Owner: <strong className="text-slate-200">{item.owner}</strong></span>
                                </div>
                                <p className="text-xs text-slate-200 font-medium leading-relaxed">
                                    {item.action}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
